import { faArrowDown, faArrowUp, faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { TMoveSectionDirection } from "redux/actions/websiteBuilder/actions/section/moveSection";
import { TWebsiteBuilderTreeRefMap } from "../../../../hooks/treeRefMap/useTreeRefMapRegistration";
import { TWebsiteBuilderSectionViewPosition } from "./WebsiteBuilderSectionView";
import { clamp } from "lodash";
import { websiteBuilderZLayers } from "../../../../websiteBuilderZLayers";
import { TWebsiteBuilderScrollContainerRef } from "../../../../WebsiteBuilderEditor";
import { TWebsiteBuilderSection } from "../../../../nodes/section/websiteBuilderSection";
import { IWebsiteBuilderRoot } from "../../../../nodes/websiteBuilderRoot";
import { IWebsiteBuilderSectionOperations } from "../../../../hooks/sectionOperations/useWebsiteBuilderSectionOperations";
import { IWebsiteBuilderContextMenuPosition } from "../../hooks/helpers/WebsiteBuilderContextMenu";

interface ISectionToolbarProps {
  node: TWebsiteBuilderSection;
  parentNode: IWebsiteBuilderRoot;
  hovered: boolean;
  position: TWebsiteBuilderSectionViewPosition;
  maxIndex: number;
  sectionOperations: IWebsiteBuilderSectionOperations;
  handleViewOpenContextMenu: (position: IWebsiteBuilderContextMenuPosition) => void;
  scrollContainerRef: TWebsiteBuilderScrollContainerRef;
  treeRefMap: TWebsiteBuilderTreeRefMap;
}

export function SectionToolbar(props: ISectionToolbarProps) {
  const {
    node,
    parentNode,
    hovered,
    position,
    maxIndex,
    sectionOperations,
    handleViewOpenContextMenu,
    scrollContainerRef,
    treeRefMap,
  } = props;

  const toolbarRef = useRef<HTMLDivElement>(null);

  const [top, setTop] = useState<number>(null);

  const handleChangeToolbarPosition = useCallback(() => {
    const sectionRect = treeRefMap.current.get(node.id)?.getBoundingClientRect();
    const containerRect = scrollContainerRef.current?.getBoundingClientRect();
    const toolbarRect = toolbarRef.current?.getBoundingClientRect();

    if (!sectionRect || !containerRect || !toolbarRect) {
      return;
    }

    const screenPositionTop = sectionRect.top + sectionRect.height / 2 - toolbarRect.height / 2;
    const screenPositionBottom = screenPositionTop + toolbarRect.height;

    const containerBuffer = 8;

    const containerTop = containerRect.top + containerBuffer;
    const containerBottom = containerRect.bottom - containerBuffer;

    let updatedTop = sectionRect.height / 2 - toolbarRect.height / 2;

    if (screenPositionTop < containerTop) {
      const delta = containerTop - screenPositionTop;
      updatedTop += delta;
    } else if (screenPositionBottom > containerBottom) {
      const delta = screenPositionBottom - containerBottom;
      updatedTop -= delta;
    }

    updatedTop = clamp(updatedTop, containerBuffer, sectionRect.height - toolbarRect.height - containerBuffer);

    setTop(updatedTop);
  }, [node.id]);

  useEffect(() => {
    if (hovered) {
      scrollContainerRef.current?.addEventListener("scroll", handleChangeToolbarPosition);

      handleChangeToolbarPosition();
    }

    return () => {
      scrollContainerRef.current?.removeEventListener("scroll", handleChangeToolbarPosition);

      setTop(null);
    };
  }, [hovered, handleChangeToolbarPosition]);

  function handleMoveSection(direction: TMoveSectionDirection) {
    if (position.type === "inner") {
      if (direction === "up") {
        sectionOperations.moveSectionUp(node, parentNode, treeRefMap, scrollContainerRef);
      } else if (direction === "down") {
        sectionOperations.moveSectionDown(node, parentNode, treeRefMap, scrollContainerRef);
      }
    }
  }

  return (
    <>
      {hovered && (
        <div
          ref={toolbarRef}
          className="website-builder-section-toolbar"
          style={{
            top: top != null ? `${top}px` : "50%",
            transform: `translate(calc(-50%), ${top != null ? "0" : "-50%"})`,
            zIndex: websiteBuilderZLayers.designSpace.sectionEditorUI,
          }}
        >
          <button onClick={() => handleMoveSection("up")} disabled={position.type === "outer" || position.index <= 0}>
            <FontAwesomeIcon className="section-toolbar-option-icon" icon={faArrowUp} />
          </button>
          <button
            onClick={() => handleMoveSection("down")}
            disabled={position.type === "outer" || position.index >= maxIndex}
          >
            <FontAwesomeIcon className="section-toolbar-option-icon" icon={faArrowDown} />
          </button>
          <button onClick={e => handleViewOpenContextMenu({ x: e.clientX, y: e.clientY })}>
            <FontAwesomeIcon className="section-toolbar-option-icon" icon={faEllipsis} />
          </button>
        </div>
      )}
    </>
  );
}
